import { ApiBaseService } from "./ApiBaseService";
import type {
    ChatChannel,
    ChatChannelAddress,
    ChannelQuery,
    Room,
    Conversation,
    RoomMessageQuery,
    TextMessageContent,
    Message,
    RoomQuery,
} from "~/models/Chat";
import type { LengthAwarePaginator } from "~/types/LengthAwarePaginator";
import type { ApiResponse } from "~/types/ApiResponse";

interface ConversationFeedback {
    rating: number;
    comment: string;
}

export default class ChatService extends ApiBaseService {
    public closeConversation(
        id: string | undefined,
        feedback: ConversationFeedback,
    ): Promise<ApiResponse<Conversation>> {
        return this.request(
            this.route(
                "chat.conversation.close",
                "/chat/conversations/{id}/close",
                {
                    id,
                },
            ),
            {
                method: "POST",
                body: { feedback },
            },
        );
    }

    public fetchRooms(
        options: RoomQuery,
    ): Promise<LengthAwarePaginator<Room<any>>> {
        return this.request<LengthAwarePaginator<Room<any>>>(
            this.route(
                "chat.rooms.list",
                options.id ? "/chat/rooms/{type}/{id}" : "/chat/rooms/{type}",
                {
                    type: options.type,
                    id: options.id,
                },
            ),
            {
                query: useOmit(options, ["type", "id"]),
            },
        );
    }

    public fetchChannels(
        options: ChannelQuery,
    ): Promise<ApiResponse<LengthAwarePaginator<ChatChannel>>> {
        return this.request<ApiResponse<LengthAwarePaginator<ChatChannel>>>(
            this.route(
                "chat.rooms.messages",
                "/chat/rooms/{type}/{id}/channels",
                {
                    type: options.room.type,
                    id: options.room.id,
                },
            ),
            {
                query: useOmit(options, ["room"]),
            },
        );
    }

    public fetchMessages(
        options: RoomMessageQuery,
    ): Promise<ApiResponse<Message[]>> {
        return this.request<ApiResponse<Message[]>>(
            this.route(
                "chat.rooms.messages",
                "/chat/rooms/{type}/{id}/messages",
                {
                    type: options.room_type,
                    id: options.room_id,
                },
            ),
            {
                query: useOmit(options, ["room_type", "room_id"]),
            },
        );
    }

    sendMessage(options: {
        room: Room;
        channel: {
            id: ChatChannel["id"];
            type: ChatChannel["type"]["value"];
            content: ChatChannel["content"]["value"];
        };
        address?:
            | {
                  id: ChatChannelAddress["id"];
                  address: ChatChannelAddress["address"];
              }
            | {
                  id: ChatChannelAddress["id"];
              }
            | {
                  address: ChatChannelAddress["address"];
              };
        content: Partial<TextMessageContent<any>>;
        additional_data?: Record<string, any>;
    }) {
        console.log(options);
        return this.request(
            this.route(
                "chat.rooms.message",
                "/chat/rooms/{type}/{id}/messages",
                {
                    type: options.room.type,
                    id: options.room.id,
                },
            ),
            {
                method: "POST",
                body: {
                    ...options?.additional_data,
                    channel: options.channel,
                    address: options.address,
                    content: options.content,
                },
            },
        );
    }
}
